/**
 * 巢狀捲動的範圍規則。
 *
 * 本專案以 Lenis（root + syncTouch）接管整頁捲動。規則是：**只有首頁列表與店家頁列表
 * 允許巢狀捲動**（它們是主要內容，捲到底後讓整頁接著捲是預期行為）；其餘所有自己會捲
 * 的區域——下拉選單、彈窗內的列表、抽屜等——手勢都應該留在該區域內，不要外溢。
 *
 * 不這樣做會有兩個症狀：
 * 1. 滑選單／彈窗列表時，背景頁面跟著動
 * 2. 往下滑時誤觸下拉重整（usePullToRefresh 監聽 window，不受 Lenis 的 prevent 影響，
 *    且它判斷「在頂端」用 window.scrollY，Lenis 接管後該值恆為 0，條件永遠成立）
 *
 * 兩個消費端共用這份定義，避免其中一邊漏掉而行為不一致：
 * - App.vue：Lenis 的 `prevent`
 * - usePullToRefresh：手勢起點落在這些區域時不啟用下拉重整
 *
 * 新增會自己捲動的區域時，記得一併加進 NESTED_SCROLL_PREVENTED_CLASS_NAMES。
 */

/**
 * 允許巢狀捲動：只有首頁列表與店家頁列表。
 *
 * BaseDataTable 在帳務查詢、商品列表等處也有使用，不能以元件為單位放行；
 * 改由呼叫端以 `allowNestedScroll` 明確宣告，元件據此掛上這個標記 class。
 */
export const NESTED_SCROLL_ALLOWED_CLASS_NAMES = [
  'base-data-table__scroll-container--nested-allowed'
] as const

/** 不交給整頁捲動機制處理的區域 */
export const NESTED_SCROLL_PREVENTED_CLASS_NAMES = [
  // ── 下拉選單／浮層 ──
  'ant-select-dropdown',
  'ant-dropdown',
  'ant-picker-dropdown',
  // ant-design-vue 選單的虛擬捲動容器（overflow 是 hidden，靠 JS + transform 自己捲）
  'rc-virtual-list-holder',
  'dropdown-options', // ProfilePage 的時區選擇
  'list-selector', // ListSelector 共用元件

  // ── 彈窗內的列表 ──
  'goods-list-container', // 更換產品
  'confirmation-list', // 批次操作／批次收款的確認清單
  'settlement-history-modal', // 結帳歷史
  'log-body', // 商品池異動紀錄
  'terms-scroll-container', // 服務條款

  // ── 抽屜與其他獨立捲動區 ──
  // ant 抽屜的捲動容器。篩選、排序、時區等各式底部抽屜都是同一個容器在捲，列這一個就
  // 涵蓋現有與日後新增的抽屜，不必逐支維護各自的 root class。
  // 這些抽屜自己有設 overscroll-behavior: contain，但那只擋瀏覽器原生的 scroll chaining，
  // Lenis 的 prevent 是另一套機制，沒列進來就會變成「在抽屜上滑動卻是背景整頁在捲」
  'ant-drawer-body',
  'timezone-drawer-list', // 首頁時區抽屜（抽屜內另一層獨立捲動區）
  'scroll-y', // quasar 的捲動容器（日期選擇器等浮層）
  'settings-container', // egm-rc 機台設定
  'draggable-container', // 群組編輯的機台清單
  'scrollable-content', // 付款確認頁

  // BaseDataTable 的捲動容器：預設攔截，只有掛上 --nested-allowed 的首頁／店家頁列表
  // 會因白名單優先而放行
  'base-data-table__scroll-container'
] as const

/** 供 `closest()` 使用的選擇器字串 */
export const NESTED_SCROLL_PREVENTED_SELECTOR = NESTED_SCROLL_PREVENTED_CLASS_NAMES.map(
  (className) => `.${className}`
).join(', ')

/** 這個節點是否屬於「不該把手勢外溢出去」的捲動區域 */
export const isNestedScrollPrevented = (node: Element | null | undefined): boolean => {
  if (!node?.classList) return false
  if (NESTED_SCROLL_ALLOWED_CLASS_NAMES.some((name) => node.classList.contains(name))) return false
  return NESTED_SCROLL_PREVENTED_CLASS_NAMES.some((name) => node.classList.contains(name))
}
